import { useReactiveVar } from '@apollo/client';
import { Route, Routes } from '@hulu/react-router-dom';
import React, { Suspense, useContext, useEffect, useState } from 'react';

import useGetAdvertisersByOrgId from '../../apis/graphql/advertisers/useGetAdvertisersByOrgId';
import useFetchOrganization from '../../apis/graphql/organization/useFetchOrganization';
import { Permissions } from '../../apis/graphql/types';
import useMyPermissions from '../../apis/graphql/user/useMyPermissions';
import { LoaderWrapp } from '../../components/GlobalWrapper/styled';
import { StyledLoader } from '../../components/styled';
import { UnauthorizedRoute } from '../../components/UnauthorizedRoute';
import { isBaymaxEnabledVar, RoutePath, selectedOrganizationIdVar, userAmsUserIdVar } from '../../constants';
import { GlobalPermissionsContext } from '../../context';
import { defaultPermissions } from '../../context/GlobalPermissions/constants';
import Login from '../../pages/Login';
import NotFound from '../../pages/NotFound';
import { getBrunoSpecificRoutes } from '../commonRoutes/brunoSpecificRoutes';
import { commonMarketerAppRoutes } from '../commonRoutes/commonMarketerAppRoutes';
import { splitPermissionsData } from './utils';

const MarketerRoutes = (): JSX.Element => {
  const { updateGlobalPermissions } = useContext(GlobalPermissionsContext);
  const selectedOrganizationId = useReactiveVar(selectedOrganizationIdVar);
  const selectedUserId = useReactiveVar(userAmsUserIdVar);
  const [myPermissions, setMyPermissions] = useState<Permissions>(defaultPermissions);

  const organization = useFetchOrganization(selectedOrganizationId);
  const { data, loading } = useMyPermissions(selectedUserId, selectedOrganizationId, {
    skipIfOrgIdEmpty: true,
  });

  // to check if external_visible advertisers exist
  const { advertisersOptions } = useGetAdvertisersByOrgId(selectedOrganizationId, ['external_visible']);
  const isBaymaxEnabled = !!advertisersOptions?.length;

  useEffect(() => {
    updateGlobalPermissions?.(splitPermissionsData(data));
    if (data?.permissions) {
      setMyPermissions(data.permissions);
    }
    isBaymaxEnabledVar(isBaymaxEnabled);
  }, [data, selectedOrganizationId, isBaymaxEnabled, updateGlobalPermissions]);

  if (!selectedOrganizationId && (!organization.data || organization.error)) {
    window.location.href = RoutePath.LOGOUT;
  }

  if (loading || organization.loading) {
    return (
      <LoaderWrapp>
        <StyledLoader data-testid="loader" />
      </LoaderWrapp>
    );
  }

  return (
    <Suspense fallback={<StyledLoader />}>
      <Routes>
        <Route path={RoutePath.HOME} element={<UnauthorizedRoute children={<Login />} />} />
        {commonMarketerAppRoutes.map((route) => (
          <Route key={route.path} {...route} />
        ))}
        {getBrunoSpecificRoutes(myPermissions).map((route) => (
          <Route key={route.path} {...route} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default MarketerRoutes;
